import React, { ChangeEvent, useCallback } from "react";
import { MiInputSelectBox } from "@miplatform/components";
import { AccountType, IAccountResponse, PaymentType } from "@miplatform/types";
import { ServerAutocomplete } from "@mibuildweb/components";
import { useAccounts } from "@mibuildweb/hooks";
import { Box, Divider, Typography } from "@mui/material";

export type optionsType = { label: string; value: string };

export type Filters = {
  paymentStatus?: string;
  paymentType?: PaymentType;
  groupBy: string;
  accountId: string;
  bankAccountQuickBooksId?: string;
};

const paymentStatusOptions: optionsType[] = [
  { label: "Fully Applied", value: "FullyPaid" },
  { label: "Not Fully Applied", value: "PartialPaid" },
];

const paymentTypeOptions: optionsType[] = [
  { label: "Other", value: PaymentType.Other },
  { label: "Credit Card", value: PaymentType.CreditCard },
  { label: "Check", value: PaymentType.Check },
  { label: "Cash", value: PaymentType.Cash },
];

type FilterComponentProps = {
  bankAccountsOptions: optionsType[];
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
};

export const PaymentListFilterComponent: React.FC<FilterComponentProps> = ({
  bankAccountsOptions,
  filters,
  setFilters,
}) => {
  const { accounts, getAccounts } = useAccounts({
    accountTypes: [AccountType.Builder, AccountType.Service],
  });
  const handlePaymentStatusChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        paymentStatus:
          event.target.value === "None" ? undefined : event.target.value,
      }));
    },
    [setFilters]
  );

  const handlePaymentTypeChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        paymentType:
          event.target.value === "None"
            ? undefined
            : (event.target.value as PaymentType),
      }));
    },
    [setFilters]
  );

  const handleCustomerChange = useCallback(
    (value: string | null) => {
      setFilters((prevFilters: Filters) => ({
        ...prevFilters,
        accountId: value || "",
      }));
    },
    [setFilters]
  );

  const handleBankAccountChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      setFilters((prev) => ({
        ...prev,
        bankAccountQuickBooksId:
          event.target.value === "None" ? undefined : event.target.value,
      }));
    },
    [setFilters]
  );

  return (
    <Box height="100%" flex={0.2}>
      <Typography variant="body2" fontWeight={600}>
        Filter & Sort
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box>
        <Typography fontSize={12} fontWeight={600} my={2}>
          Payment Status
        </Typography>
        <MiInputSelectBox
          options={paymentStatusOptions}
          value={filters.paymentStatus}
          handleChange={(event: ChangeEvent<HTMLSelectElement>) =>
            handlePaymentStatusChange(event)
          }
        />
        <Typography fontSize={12} fontWeight={600} my={2}>
          Customer
        </Typography>
        <ServerAutocomplete<IAccountResponse>
          name="customerOptions"
          value={accounts.find((opt) => opt.id === filters.accountId) || null}
          labelKey="name"
          filterKeys={["name"]}
          options={accounts}
          getter={getAccounts}
          onChange={handleCustomerChange}
        />

        <Typography fontSize={12} fontWeight={600} my={2}>
          Payment Type
        </Typography>
        <MiInputSelectBox
          options={paymentTypeOptions}
          value={filters.paymentType}
          handleChange={handlePaymentTypeChange}
        />

        <Typography fontSize={12} fontWeight={600} my={2}>
          Bank Account
        </Typography>
        <MiInputSelectBox
          options={bankAccountsOptions}
          value={filters.bankAccountQuickBooksId}
          handleChange={(event: ChangeEvent<HTMLSelectElement>) =>
            handleBankAccountChange(event)
          }
        />
      </Box>
    </Box>
  );
};
